// Copy for the Meta-ads landing page (/parents-recus-retours,
// /en/parents-receipts-returns). Single source of truth — consumed by the page
// components, the runtime SEO head and the build-time prerender head.

export interface LandingFaqItem {
  q: string;
  a: string;
}

export interface LandingCopy {
  meta: { title: string; description: string };
  hero: {
    title: string;
    subtitle: string;
    cta: string;
    reassurance: string;
    mockupAlt: string;
    familyAlt: string;
  };
  /** App Store rating trust signal, shown in the hero badge and sticky CTA. */
  rating: { value: string; label: string };
  ctaShort: string;
  stickyHint: string;
  midCta: {
    text: string;
    cta: string;
  };
  benefits: {
    title: string;
    items: { title: string; text: string }[];
  };
  testimonials: {
    title: string;
    /** App Store-style review cards — title is the short headline above the quote. */
    items: { title: string; quote: string; name: string; detail: string }[];
  };
  how: {
    title: string;
    steps: string[];
  };
  secondCta: {
    title: string;
    cta: string;
  };
  faq: {
    title: string;
    items: LandingFaqItem[];
  };
  footer: {
    tagline: string;
    privacy: string;
    terms: string;
    support: string;
    rights: string;
  };
  switchLabel: string;
  switchTitle: string;
}

export const landingParents: Record<"fr" | "en", LandingCopy> = {
  fr: {
    meta: {
      title: "ReFound — T'as gardé le reçu? | App de reçus pour les familles",
      description:
        "ReFound garde vos reçus importants au même endroit : retours, garanties et vie de famille. Retrouvez le bon reçu en quelques secondes. Gratuit sur iPhone et Android.",
    },
    hero: {
      title: "T'as gardé le reçu?",
      subtitle:
        "Scannez vos reçus une fois. Retrouvez le bon en 2 secondes quand vient le temps de retourner, d'échanger ou de faire jouer la garantie.",
      cta: "Télécharger gratuitement",
      reassurance: "Gratuit · Installée en 30 secondes · Pensé pour les familles d'ici",
      mockupAlt: "L'application ReFound affichant un reçu enrichi avec les détails du produit",
      familyAlt: "Une famille détendue à la maison, un parent tenant son téléphone",
    },
    rating: { value: "5,0", label: "sur l'App Store" },
    ctaShort: "Télécharger — gratuit",
    stickyHint: "ReFound, gratuit sur iPhone et Android",
    midCta: {
      text: "Le prochain retour sera tellement plus simple.",
      cta: "Télécharger gratuitement",
    },
    benefits: {
      title: "Pourquoi télécharger ReFound?",
      items: [
        {
          title: "Retrouvez vos reçus en quelques secondes",
          text: "Fini de fouiller dans les tiroirs et les poches de manteau. Tout est dans votre téléphone.",
        },
        {
          title: "Gérez les retours plus facilement",
          text: "Le bon reçu, prêt à présenter au comptoir quand un article ne fait pas l'affaire.",
        },
        {
          title: "Gardez vos garanties importantes au même endroit",
          text: "Les détails qui comptent, accessibles le jour où un produit vous lâche.",
        },
      ],
    },
    testimonials: {
      title: "Des parents qui ne perdent plus leurs reçus",
      items: [
        {
          title: "Les bottes trop petites",
          quote:
            "J'ai retourné les bottes trop petites de mon gars sans stresser — le reçu était déjà dans mon téléphone.",
          name: "Marie-Ève",
          detail: "maman de deux",
        },
        {
          title: "Le reçu partagé en deux secondes",
          quote:
            "Mon conjoint était au magasin, je lui ai partagé le reçu en deux secondes. Fini les allers-retours.",
          name: "Sophie",
          detail: "Laval",
        },
        {
          title: "La garantie retrouvée tout de suite",
          quote:
            "La cafetière a lâché après six mois. J'ai retrouvé la preuve d'achat et la garantie tout de suite.",
          name: "Patrick",
          detail: "papa de trois",
        },
      ],
    },
    how: {
      title: "Comment ça marche",
      steps: [
        "Scannez votre reçu",
        "ReFound l'organise et l'enrichit",
        "Retrouvez-le quand vous devez retourner, échanger ou vérifier une garantie",
      ],
    },
    secondCta: {
      title: "Moins de reçus perdus. Moins de retours compliqués.",
      cta: "Télécharger ReFound",
    },
    faq: {
      title: "Questions fréquentes",
      items: [
        {
          q: "Est-ce que ReFound remplace le reçu papier?",
          a: "ReFound garde une version numérique claire de vos reçus pour les retrouver facilement. Gardez le reçu papier si un marchand l'exige, mais l'essentiel reste toujours sur votre téléphone.",
        },
        {
          q: "Est-ce que je peux utiliser ReFound pour un retour en magasin?",
          a: "Oui. Vous retrouvez le bon reçu, affichez son image et le code-barres quand il est disponible pour le présenter au comptoir. L'acceptation du retour dépend de la politique du marchand.",
        },
        {
          q: "Est-ce que mes reçus sont privés?",
          a: "Oui. Vos reçus restent privés et protégés. Les images sauvegardées dans le cloud sont conservées dans un stockage privé et ne sont pas publiquement accessibles. Vos données ne sont jamais vendues et vous pouvez les supprimer à tout moment.",
        },
        {
          q: "Est-ce que l'app est gratuite?",
          a: "Oui. ReFound est gratuit à télécharger et les fonctions essentielles sont gratuites.",
        },
      ],
    },
    footer: {
      tagline: "Vos reçus, au même endroit.",
      privacy: "Confidentialité",
      terms: "Conditions",
      support: "Aide",
      rights: "Tous droits réservés.",
    },
    switchLabel: "EN",
    switchTitle: "Switch to English",
  },
  en: {
    meta: {
      title: "ReFound — Did you keep the receipt? | Receipt app for families",
      description:
        "ReFound keeps your important receipts in one place: returns, warranties and family life. Find the right receipt in seconds. Free on iPhone and Android.",
    },
    hero: {
      title: "Did you keep the receipt?",
      subtitle:
        "Scan your receipts once. Find the right one in 2 seconds when it's time to return, exchange or claim a warranty.",
      cta: "Download for free",
      reassurance: "Free · Installed in 30 seconds · Built for real family life",
      mockupAlt: "The ReFound app showing an enriched receipt with product details",
      familyAlt: "A relaxed family at home, a parent holding their phone",
    },
    rating: { value: "5.0", label: "on the App Store" },
    ctaShort: "Download — free",
    stickyHint: "ReFound, free on iPhone and Android",
    midCta: {
      text: "Your next return will be so much easier.",
      cta: "Download for free",
    },
    benefits: {
      title: "Why download ReFound?",
      items: [
        {
          title: "Find your receipts in seconds",
          text: "No more digging through drawers and coat pockets. It's all on your phone.",
        },
        {
          title: "Handle returns more easily",
          text: "The right receipt, ready to show at the counter when something doesn't work out.",
        },
        {
          title: "Keep your important warranties in one place",
          text: "The details that matter, ready the day a product lets you down.",
        },
      ],
    },
    testimonials: {
      title: "Parents who stopped losing their receipts",
      items: [
        {
          title: "The too-small boots",
          quote:
            "I returned my son's too-small boots without any stress — the receipt was already on my phone.",
          name: "Marie-Eve",
          detail: "mom of two",
        },
        {
          title: "Receipt shared in two seconds",
          quote:
            "My partner was at the store, I shared the receipt in two seconds. No more back and forth.",
          name: "Sophie",
          detail: "Laval",
        },
        {
          title: "Warranty found right away",
          quote:
            "The coffee maker died after six months. I found the proof of purchase and warranty right away.",
          name: "Patrick",
          detail: "dad of three",
        },
      ],
    },
    how: {
      title: "How it works",
      steps: [
        "Scan your receipt",
        "ReFound organizes and enriches it",
        "Find it when you need to return, exchange or check a warranty",
      ],
    },
    secondCta: {
      title: "Fewer lost receipts. Fewer complicated returns.",
      cta: "Download ReFound",
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          q: "Does ReFound replace the paper receipt?",
          a: "ReFound keeps a clear digital version of your receipts so they're easy to find. Keep the paper receipt if a retailer requires it, but the essentials stay on your phone.",
        },
        {
          q: "Can I use ReFound for an in-store return?",
          a: "Yes. You find the right receipt, show its image and the barcode when available to present at the counter. Whether the return is accepted depends on the retailer's policy.",
        },
        {
          q: "Are my receipts private?",
          a: "Yes. Your receipts stay private and protected. Images backed up to the cloud are kept in private storage and are not publicly accessible. Your data is never sold and you can delete it at any time.",
        },
        {
          q: "Is the app free?",
          a: "Yes. ReFound is free to download and the core features are free.",
        },
      ],
    },
    footer: {
      tagline: "Your receipts, in one place.",
      privacy: "Privacy",
      terms: "Terms",
      support: "Help",
      rights: "All rights reserved.",
    },
    switchLabel: "FR",
    switchTitle: "Passer au français",
  },
};
